import { useState, useEffect, useMemo, useRef } from 'react';
import { Marker as LeafletMarker } from 'leaflet';
import { IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { enqueueSnackbar, closeSnackbar } from 'notistack';

import usePostNewPosition from 'hooks/Object/usePostNewPosition';
import { ObjectItem } from 'api/clients/types';

interface UseMarkerLogicOptions {
  object_data: ObjectItem;
  isDraggable: boolean;
  onDragEnd?: (objData: ObjectItem, newPos: [number, number]) => void;
  onToggleDraggable?: (objData: ObjectItem) => void;
  onSavePositionSuccess?: (objData: ObjectItem) => void;
}

export default function useCustomMarkerFuncs({
  object_data,
  isDraggable,
  onDragEnd,
  onToggleDraggable,
  onSavePositionSuccess,
}: UseMarkerLogicOptions) {
  const [markerPosition, setMarkerPosition] = useState<[number, number]>(object_data.position || [0, 0]);

  const markerRef = useRef<LeafletMarker>(null);

  const { isLoading, setNewPosition } = usePostNewPosition();

  useEffect(() => {
    setMarkerPosition(object_data.position || [0, 0]);
  }, [object_data.position]);

  const eventHandlers = useMemo(
    () => ({
      dragend() {
        if (!markerRef.current) return;
        const latLng = markerRef.current.getLatLng();
        const newPos: [number, number] = [latLng.lat, latLng.lng];

        setMarkerPosition(newPos);
        onDragEnd?.(object_data, newPos);

        setTimeout(() => {
          markerRef.current?.openPopup();
        }, 200);
      },
    }),
    [object_data, onDragEnd],
  );

  useEffect(() => {
    if (isDraggable && markerRef.current && (markerRef.current as any)._map) {
      markerRef.current.openPopup();
    }
  }, [isDraggable]);

  // Force disable dragging when isDraggable is false
  useEffect(() => {
    if (markerRef.current) {
      if (isDraggable) {
        markerRef.current.dragging?.enable();
      } else {
        markerRef.current.dragging?.disable();
      }
    }
  }, [isDraggable]);

  function handleToggleDraggable() {
    onToggleDraggable?.(object_data);
  }

  function handleSavePosition() {
    const payload = {
      object_slug: object_data.slug,
      latitude: markerPosition[0],
      longitude: markerPosition[1],
    };

    setNewPosition(payload, {
      onSuccess: () => {
        enqueueSnackbar('Position updated!', {
          variant: 'success',
          action: key => (
            <IconButton onClick={() => closeSnackbar(key)} color="inherit">
              <CloseIcon />
            </IconButton>
          ),
        });
        // Force disable dragging immediately after save
        if (markerRef.current) {
          markerRef.current.dragging?.disable();
        }
        onSavePositionSuccess?.(object_data);
      },
      onError: (err: any) => {
        enqueueSnackbar(`Failed to save position: ${err?.message || 'Unknown error'}`, {
          variant: 'error',
        });
      },
    });
  }

  return {
    markerRef,
    markerPosition,
    isLoading,
    handleSavePosition,
    handleToggleDraggable,
    eventHandlers,
  };
}
